import { AuditLogsAgGridWrapper, CustomTabs, getActionViewAuditLogStaticFilters, checkType, CtyShowToast, McpToolMappingControls, useMcpToolEditorState } from "@circuitry-ai/doc-ui";
import { useState, useEffect, Key, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ActionConfiguration } from "./actionconfiguration";
import { ActionExecute } from "./actionexecute";
import { buildActionMcpUpsertPayload, resolveMcpUpsertSnapshot, parseInputSchemaJson, normalizeMcpCapabilityName, resolveMcpToolMappingEnabled, type ActionMcpUpsertSnapshot } from "@circuitry-ai/doc-data";
import { EnableMcpToolModal, type McpToolVisibility } from "./EnableMcpToolModal";
import { RouteState, useEntityResourcePermissions, resolveTenantIdFromToken, useGetCapabilityBindingQuery, useGetTenantToolsQuery, useUpdateMcpToolMutation, useUpsertActionToolMutation, type RootState } from "@circuitry-ai/doc-data";


export enum ActionViewTabs {
    CONFIGURATION = 'configuration',
    EXECUTE = 'execute',
    AUDIT_LOGS = 'audit-logs',
    MCP_TOOL_MAPPING = 'mcp-tool-mapping',
}

const tabSpecificSubText: Record<string, string> = {
    [ActionViewTabs.CONFIGURATION]:
        'Configure the handle function, API body, tool mappings, and UXGenie template for this action.',
    [ActionViewTabs.EXECUTE]: 'Run and test this action.',
    [ActionViewTabs.AUDIT_LOGS]: 'View audit history for this action.',
    [ActionViewTabs.MCP_TOOL_MAPPING]: 'Enable and configure MCP tool mapping for this action.',
};

function getDisabledActionViewTabs(isCreateMode: boolean, canUpdate: boolean): ActionViewTabs[] {
    if (isCreateMode) {
        return [ActionViewTabs.AUDIT_LOGS, ActionViewTabs.MCP_TOOL_MAPPING];
    }
    if (canUpdate) {
        return [];
    }
    return [ActionViewTabs.CONFIGURATION];
}

export interface ActionViewProps { }

export function ActionView(props: ActionViewProps) {

    const { t } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();
    const { mode } = useParams<{ mode: string; }>();
    const tab = new URLSearchParams(location.search).get('tab');
    const { action } = location?.state ?? {};
    const token = useSelector((state: RootState) => state.auth.token);
    const actionResourceId = action?.source_id || action?.aiworker_id;
    const headerSourceId = action?.source_id ?? null;
    const actionViewAuditLogFilters = useMemo(
        () => (headerSourceId ? getActionViewAuditLogStaticFilters(headerSourceId) : []),
        [headerSourceId]
    );
    const isCreateMode = mode === RouteState.CREATE;
    const { canUpdate } = useEntityResourcePermissions('action', isCreateMode ? undefined : actionResourceId);
    const [mcpToolMappingEnabled, setMcpToolMappingEnabled] = useState<boolean>(false);
    const [actionTitle, setActionTitle] = useState<string>(action?.name ?? '');
    const mcpUpsertSnapshotRef = useRef<(() => ActionMcpUpsertSnapshot | null) | null>(null);
    const userToggledMcpSwitchRef = useRef(false);
    const [upsertActionTool, { isLoading: isMcpUpserting }] = useUpsertActionToolMutation();
    const [updateMcpTool, { isLoading: isMcpUpdating }] = useUpdateMcpToolMutation();
    const actionIdentity = action?.source_id ?? action?.name ?? '';
    const tenantId = useMemo(() => resolveTenantIdFromToken(token ?? ''), [token]);
    const capabilityName = normalizeMcpCapabilityName(action?.name ?? actionTitle);
    const {
        data: capabilityBinding,
        isFetching: isBindingFetching,
        isSuccess: isBindingLoaded,
    } = useGetCapabilityBindingQuery(
        { tenantId, capabilityName },
        {
            skip: isCreateMode || !tenantId || !capabilityName,
            refetchOnMountOrArgChange: true,
        },
    );
    const existingCapability = capabilityBinding?.capability;
    const { data: tenantTools } = useGetTenantToolsQuery(tenantId, {
        skip: isCreateMode || !tenantId,
        refetchOnMountOrArgChange: true,
    });
    const matchedTenantTool = useMemo(
        () =>
            tenantTools?.find(
                (tool) => normalizeMcpCapabilityName(tool.name) === capabilityName,
            ),
        [tenantTools, capabilityName],
    );

    function getTabiItems() {
        const tabItems = [
            { key: ActionViewTabs.CONFIGURATION, title: 'Configuration', id: 'configuration-tab', canView: true },
            { key: ActionViewTabs.EXECUTE, title: 'Execute', id: 'execute-tab', canView: !isCreateMode },
            { key: ActionViewTabs.AUDIT_LOGS, title: t('Audit Logs'), id: 'action-view-audit-logs-tab', canView: !isCreateMode },
            {
                key: ActionViewTabs.MCP_TOOL_MAPPING,
                title: t('MCP Tool Mapping'),
                id: 'action-view-mcp-tool-mapping-tab',
                canView: !isCreateMode,
            },
        ];
        return tabItems;
    }

    const disabledKeys = useMemo(
        () => getDisabledActionViewTabs(isCreateMode, canUpdate),
        [isCreateMode, canUpdate],
    );

    const [currentTab, setCurrentTab] = useState<string>(tab ?? ActionViewTabs.CONFIGURATION);

    const onTabChange = (tab: Key) => {
        setCurrentTab(tab as string);
        navigate(`/actions/${mode}?tab=${tab}`, { state: { action } });
    };

    useEffect(() => {
        if (!isCreateMode && disabledKeys.includes(currentTab as ActionViewTabs)) {
            onTabChange(ActionViewTabs.EXECUTE);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [disabledKeys, currentTab, isCreateMode]);

    useEffect(() => {
        userToggledMcpSwitchRef.current = false;
        setMcpToolMappingEnabled(false);
    }, [actionIdentity, capabilityName, isCreateMode]);

    useEffect(() => {
        if (userToggledMcpSwitchRef.current || isCreateMode) return;
        if (!capabilityName || isBindingFetching || !isBindingLoaded) return;

        setMcpToolMappingEnabled(
            resolveMcpToolMappingEnabled(capabilityBinding, matchedTenantTool),
        );
    }, [
        isCreateMode,
        capabilityName,
        capabilityBinding,
        matchedTenantTool,
        isBindingFetching,
        isBindingLoaded,
    ]);

    useEffect(() => {
        setActionTitle(action?.name ?? '');
    }, [actionIdentity, action?.name]);

    useEffect(() => {
        if (!actionTitle.trim()) {
            setMcpToolMappingEnabled(false);
        }
    }, [actionTitle]);

    const isMcpToolMappingSwitchDisabled =
        !actionTitle.trim() || !headerSourceId || isMcpUpserting || isMcpUpdating;
    const hasRegisteredMcpTool = Boolean(existingCapability || matchedTenantTool?.capability_id);
    const canEditMcpToolMapping =
        canUpdate
        && !isBindingFetching
        && (hasRegisteredMcpTool || mcpToolMappingEnabled);

    const getMcpUpsertSnapshot = (): ActionMcpUpsertSnapshot | null =>
        resolveMcpUpsertSnapshot(mcpUpsertSnapshotRef.current?.(), action);

    const {
        isOpen: isEnableMcpModalOpen,
        isUpdateMode: isMcpUpdateMode,
        enableAfterUpdate: enableAfterMcpUpdate,
        defaults: mcpModalDefaults,
        open: openMcpModal,
        close: closeMcpModal,
        onOpenChange: setIsEnableMcpModalOpen,
    } = useMcpToolEditorState({
        capability: existingCapability,
        capabilityBinding,
        tenantTool: matchedTenantTool,
        getFallbackDescription: () => getMcpUpsertSnapshot()?.description ?? '',
        includeInputSchema: true,
    });

    const handleMcpSwitchChange = async (value: boolean) => {
        const snapshot = getMcpUpsertSnapshot();

        if (!tenantId || !snapshot) {
            CtyShowToast(
                checkType.error,
                t('Unable to update MCP tool mapping. Save the action first.'),
            );
            return;
        }

        if (value) {
            openMcpModal(Boolean(existingCapability), Boolean(existingCapability));
            return;
        }

        await syncMcpToolMapping(snapshot, false);
    };

    const syncMcpToolMapping = async (
        snapshot: ActionMcpUpsertSnapshot,
        enabled: boolean,
        overrides?: {
            description?: string;
            inputSchemaJson?: string;
            visibility?: McpToolVisibility;
        },
    ) => {
        try {
            const inputSchema = overrides?.inputSchemaJson !== undefined
                ? parseInputSchemaJson(overrides.inputSchemaJson)
                : [];

            const mcpPayload = buildActionMcpUpsertPayload({
                ...snapshot,
                mcpToolMappingEnabled: enabled,
                mcpDescription: overrides?.description,
                mcpVisibility: overrides?.visibility,
                inputSchema,
            });
            await upsertActionTool({ tenantId, ...mcpPayload }).unwrap();
            userToggledMcpSwitchRef.current = true;
            setMcpToolMappingEnabled(enabled);
            CtyShowToast(
                checkType.success,
                enabled ? t('MCP tool mapping enabled') : t('MCP tool mapping disabled'),
            );
        } catch (mcpError) {
            CtyShowToast(
                checkType.error,
                (mcpError as Error)?.message ?? t('Failed to sync MCP tool mapping'),
            );
            throw mcpError;
        }
    };

    const handleEnableMcpConfirm = async (
        description: string,
        inputSchemaJson: string,
        visibility: McpToolVisibility,
    ) => {
        const snapshot = getMcpUpsertSnapshot();

        if (!tenantId || !snapshot) {
            CtyShowToast(
                checkType.error,
                t('Unable to update MCP tool mapping. Save the action first.'),
            );
            return;
        }

        try {
            if (isMcpUpdateMode && existingCapability) {
                await updateMcpTool({
                    tenantId,
                    capability_name: existingCapability.name,
                    description,
                    visibility,
                    parameters: parseInputSchemaJson(inputSchemaJson),
                }).unwrap();

                // Update API does not toggle enabled; enable via upsert when opened from the switch.
                if (enableAfterMcpUpdate && !mcpToolMappingEnabled) {
                    await syncMcpToolMapping(snapshot, true, {
                        description,
                        inputSchemaJson,
                        visibility,
                    });
                } else {
                    CtyShowToast(checkType.success, t('MCP tool updated successfully'));
                }

                closeMcpModal();
                return;
            }

            await syncMcpToolMapping(snapshot, true, { description, inputSchemaJson, visibility });
            closeMcpModal();
        } catch (mcpError) {
            if (isMcpUpdateMode) {
                CtyShowToast(
                    checkType.error,
                    (mcpError as Error)?.message ?? t('Failed to update MCP tool'),
                );
            }
            // Upsert errors are handled in syncMcpToolMapping.
        }
    };

    return (
        <div className="flex flex-col h-full">
            <div className="relative flex items-start  gap-3">
                <div className="min-w-0 flex-1">
                    <h1
                        data-testid="action-name"
                        id="action-name"
                        className="text-xl font-bold"
                    >
                        {isCreateMode ? t('New Action') : action?.name}
                    </h1>
                    <p className="text-base font-normal text-default-500 mt-1">
                        {t(tabSpecificSubText[currentTab] ?? tabSpecificSubText[ActionViewTabs.CONFIGURATION])}
                    </p>
                </div>
            </div>
            <div className="advisor-tab-list">
                <CustomTabs
                    tabs={getTabiItems()}
                    selectedKey={currentTab}
                    onTabChange={onTabChange}
                    disabledKeys={disabledKeys}
                />
            </div>
            <div className="flex-1 overflow-y-auto">
                <div className={currentTab === ActionViewTabs.CONFIGURATION ? 'contents' : 'hidden'}>
                    <ActionConfiguration
                        onActionTitleChange={setActionTitle}
                        mcpUpsertSnapshotRef={mcpUpsertSnapshotRef}
                    />
                </div>
                {currentTab === ActionViewTabs.EXECUTE && <ActionExecute />}
                {currentTab === ActionViewTabs.AUDIT_LOGS && headerSourceId && (
                    <div className="pt-4">
                        <AuditLogsAgGridWrapper staticFilters={actionViewAuditLogFilters} />
                    </div>
                )}
                {currentTab === ActionViewTabs.MCP_TOOL_MAPPING && (
                    <div className="pt-4">
                        <div className="flex items-center gap-2">
                            <McpToolMappingControls
                                isEnabled={mcpToolMappingEnabled}
                                isSwitchDisabled={isMcpToolMappingSwitchDisabled}
                                canEdit={canEditMcpToolMapping}
                                isLoading={isMcpUpserting || isMcpUpdating}
                                onEdit={() => openMcpModal(true)}
                                onSwitchChange={handleMcpSwitchChange}
                            />
                        </div>
                    </div>
                )}
            </div>
            <EnableMcpToolModal
                isOpen={isEnableMcpModalOpen}
                onOpenChange={setIsEnableMcpModalOpen}
                defaultDescription={mcpModalDefaults.description}
                defaultInputSchemaJson={mcpModalDefaults.inputSchemaJson}
                defaultVisibility={mcpModalDefaults.visibility}
                isUpdateMode={isMcpUpdateMode}
                isLoading={isMcpUpserting || isMcpUpdating}
                onConfirm={handleEnableMcpConfirm}
            />
        </div>
    );
}